/**
 * "Message these people" from the schedule (backlog #51): who is on a set of shifts, and the message sent to them.
 * The server resolves the people from the shifts (never trusts a list of names from the browser), checks each one is
 * in the sender's scope, and freezes the list when the message goes.
 */
import { DateTime } from 'luxon';

export type ShiftMessageMode = 'group_dm' | 'channel' | 'announcement';
/** Where the sender started: one shift (or its time slot), selected shifts, a day column, the filtered view, attendance. */
export type ShiftMessageSource = 'shift' | 'slot' | 'selection' | 'day' | 'view' | 'attendance';

/** Group DMs hold up to 50 people including the sender (the workspace can set a lower limit). */
export const SHIFT_MESSAGE_GROUP_DM_MAX = 49;
export const SHIFT_MESSAGE_MAX = 500;
/** t509u: files attached to one shift message (photos, PDFs, Office files... the chat allow-list, 50 MB each). */
export const SHIFT_MESSAGE_ATTACHMENTS_MAX = 10;

export interface ShiftAudienceInput {
  source: ShiftMessageSource;
  /** What the sender saw, e.g. "Everyone 09:00–17:00 on Tue 20 Oct at Main Warehouse". Stored with the message. */
  label: string;
  /** Shifts picked directly (one shift, a selection, a day column, the filtered view, attendance rows). */
  shift_ids?: string[];
  /** Every shift with the same date, building and times as this one, and the same position unless any_position. */
  match?: { shift_id: string; any_position?: boolean };
  /** Also people on draft (unpublished) shifts. Only honoured where the sender can see drafts. */
  include_drafts?: boolean;
  /** The filters and date range on screen, kept for the record only. */
  filters?: Record<string, unknown>;
  /** Channel posts: the channel, so the preview can say who isn't in it. */
  conversation_id?: string | null;
}

export interface ShiftRecipient {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  /** Their shifts in this selection. */
  shift_ids: string[];
  /** "09:00–17:00 Cashier" for their first shift in the selection. */
  shift_label: string;
  /** Only on draft shifts (included because include_drafts was asked for). */
  draft_only: boolean;
}

export interface ShiftAudienceExcluded {
  /** Open shifts (nobody on them yet). */
  open: number;
  /** People only on draft shifts, left out because drafts weren't included. */
  drafts: number;
  /** On approved leave that day. */
  on_leave: { user_id: string; display_name: string }[];
  /** Shifts at buildings, or people in teams, outside the sender's scope. */
  out_of_scope: number;
  /** The sender is on one of the shifts (you don't message yourself). */
  self: boolean;
}

export interface ShiftAudience {
  recipients: ShiftRecipient[];
  /** The shifts the recipients are on (what the "Message sent" note is logged against). */
  shift_ids: string[];
  excluded: ShiftAudienceExcluded;
  /** The sender may see drafts for at least one of these shifts. */
  can_include_drafts: boolean;
  /** Drafts are among the shifts (so the option is worth showing). */
  has_drafts: boolean;
  /** Ready-made shift details for the message, e.g. "Shift: Tue 20 Oct, 09:00–17:00 · Cashier · Main Warehouse". */
  details: string;
  /** With conversation_id: recipients who aren't in that channel (they won't be @mentioned or see it). */
  not_in_channel?: { user_id: string; display_name: string }[];
}

export interface ShiftMessageInput extends ShiftAudienceInput {
  /** The final list, after the sender removed anyone. Must be a subset of the resolved audience. */
  user_ids: string[];
  mode: ShiftMessageMode;
  body: string;
  /** mode 'announcement': its title. */
  title?: string;
  require_ack?: boolean;
  ack_due_at?: string | null;
  include_shift_details?: boolean;
  /**
   * t509u: uploads made from the dialog (purpose 'shift_message', by the sender, ready, not used yet). They go on the
   * group message, the channel post or the announcement, whichever is chosen. The message may be just files.
   */
  attachment_ids?: string[];
}

export interface ShiftMessageResult {
  id: string;
  mode: ShiftMessageMode;
  recipients: number;
  conversation_id: string | null;
  message_id: string | null;
  announcement_id: string | null;
  /** Where to go to see it. */
  link: string;
  /** mode 'channel': recipients who aren't members, so weren't @mentioned and won't see it. */
  not_in_channel: { user_id: string; display_name: string }[];
}

/** A "Message sent to N people" note on a shift (managers only). */
export interface ShiftMessageNote {
  id: string;
  sender_id: string;
  sender_name: string;
  mode: ShiftMessageMode;
  recipients: number;
  label: string;
  require_ack: boolean;
  link: string | null;
  created_at: string;
}

export const SHIFT_MESSAGE_MODE_LABELS: Record<ShiftMessageMode, string> = {
  group_dm: 'Group message', channel: 'Post in a channel', announcement: 'Announcement',
};

export function shiftMessageNoteText(n: Pick<ShiftMessageNote, 'recipients' | 'mode' | 'sender_name'>): string {
  const how = n.mode === 'group_dm' ? 'a group message' : n.mode === 'channel' ? 'a channel post' : 'an announcement';
  return `${n.sender_name} sent ${how} to ${n.recipients} ${n.recipients === 1 ? 'person' : 'people'}`;
}

export interface ShiftDetailRow { starts_at: string; ends_at: string; location_name: string; position_name: string | null; people: number }

const day = (iso: string, tz: string) => DateTime.fromISO(iso, { zone: tz }).setLocale('en-GB');
const hm = (iso: string, tz: string) => day(iso, tz).toFormat('HH:mm');

/**
 * Shift details to add to the message. One time slot reads as a single line; several are listed (up to 6) so
 * everyone can see which shift the message is about.
 */
export function shiftDetailsText(rows: ShiftDetailRow[], tz = 'Europe/London'): string {
  if (!rows.length) return '';
  const key = (r: ShiftDetailRow) => `${r.starts_at}|${r.ends_at}|${r.location_name}|${r.position_name ?? ''}`;
  const slots = new Map<string, ShiftDetailRow>();
  for (const r of rows) {
    const k = key(r), cur = slots.get(k);
    slots.set(k, cur ? { ...cur, people: cur.people + r.people } : { ...r });
  }
  const sorted = [...slots.values()].sort((a, b) => a.starts_at.localeCompare(b.starts_at) || (a.position_name ?? '').localeCompare(b.position_name ?? ''));
  const line = (r: ShiftDetailRow) => [`${day(r.starts_at, tz).toFormat('ccc d LLL')}, ${hm(r.starts_at, tz)}–${hm(r.ends_at, tz)}`, r.position_name, r.location_name].filter(Boolean).join(' · ');
  // Same date, times and building but different positions: one line, positions listed.
  const sameTime = sorted.every(r => r.starts_at === sorted[0]!.starts_at && r.ends_at === sorted[0]!.ends_at && r.location_name === sorted[0]!.location_name);
  if (sorted.length === 1) return `Shift: ${line(sorted[0]!)}`;
  if (sameTime) {
    const r = sorted[0]!;
    return `Shift: ${day(r.starts_at, tz).toFormat('ccc d LLL')}, ${hm(r.starts_at, tz)}–${hm(r.ends_at, tz)} · ${r.location_name}`;
  }
  const shown = sorted.slice(0, 6).map(r => `• ${line(r)}`);
  if (sorted.length > 6) shown.push(`• and ${sorted.length - 6} more`);
  return `Shifts:\n${shown.join('\n')}`;
}

/** "Message everyone working Tue 20 Oct" */
export const dayMessageLabel = (date: string) => `Message everyone working ${DateTime.fromISO(date).setLocale('en-GB').toFormat('ccc d LLL')}`;
